import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { StyleSheet } from "react-native";
import { Icon } from "../icon/Icon";
import { LinearGradient } from "../linearGradient/LinearGradient";
import { Text } from "../text/Text";
import { style as buttonStyle } from "./Button.style";
import "./Button.web.css";
const start = { x: 0, y: 1 };
const end = { x: 1, y: 0 };
const ACTIVE_OPACITY = 0.6;
const toPx = (val) => {
    if (typeof val === "number")
        return `${val}px`;
    if (typeof val === "string")
        return val;
    return undefined;
};
const Button = ({ children, busy, disabled, icon, level = 3, size = "medium", variant = "primary", style: customStyle, testID, onPress, }) => {
    const containerRef = useRef(null);
    const [pressed, setPressed] = useState(false);
    const [busyWidth, setBusyWidth] = useState(0);
    const style = useMemo(() => buttonStyle({
        variant,
        disabled,
        size,
    }), [variant, disabled, size]);
    useLayoutEffect(() => {
        if (!containerRef.current)
            return;
        const observer = new ResizeObserver(([entry]) => {
            setBusyWidth(entry.contentRect.width);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);
    const handlePointerEnter = useCallback(() => {
        if (containerRef.current) {
            setBusyWidth(containerRef.current.offsetWidth);
        }
    }, []);
    const baseStyle = useMemo(() => ({
        boxSizing: "border-box",
        paddingLeft: toPx(style.base.paddingLeft),
        paddingRight: toPx(style.base.paddingRight),
        height: toPx(style.base.height),
        borderRadius: toPx(style.base.borderRadius),
        ...(style.base.borderWidth != null && style.base.borderWidth > 0
            ? {
                borderWidth: toPx(style.base.borderWidth),
                borderStyle: "solid",
                borderColor: style.base.borderColor,
            }
            : {}),
    }), [style.base]);
    const busyBarStyle = useMemo(() => ({
        position: "absolute",
        top: 0,
        left: 0,
        height: "300%",
        backgroundColor: style.busy.backgroundColor,
        opacity: 0.7,
        pointerEvents: "none",
        width: busy ? busyWidth : 0,
        transition: "width 800ms cubic-bezier(0.45, 0, 0.55, 1)",
    }), [busy, busyWidth, style.busy.backgroundColor]);
    const handlePressEnd = useCallback(() => setPressed(false), []);
    return (React.createElement("button", { ref: containerRef, "aria-busy": busy ? "true" : "false", className: "button-reset", "data-testid": testID, disabled: disabled || busy, type: "button", style: {
            cursor: disabled ? "default" : "pointer",
            display: "flex",
            width: "100%",
            minWidth: 0,
            height: toPx(style.container.height),
            borderRadius: toPx(style.base.borderRadius),
            overflow: "hidden",
            position: "relative",
            opacity: pressed ? ACTIVE_OPACITY : 1,
            ...(customStyle?.button ? StyleSheet.flatten(customStyle.button) : undefined),
        }, onClick: onPress, onPointerCancel: handlePressEnd, onPointerEnter: handlePointerEnter, onPointerLeave: handlePressEnd, onPointerUp: handlePressEnd, onPointerDown: () => {
            if (!disabled && !busy)
                setPressed(true);
        } },
        React.createElement(LinearGradient, { className: "button-gradient", colors: style.colors, end: end, start: start, style: { ...baseStyle, flexGrow: 0, flexShrink: 0 } },
            React.createElement("div", { "data-testid": "button-busy", style: busyBarStyle, "aria-hidden": true }),
            React.createElement("div", { className: "button-content" },
                icon && React.createElement(Icon, { name: icon, style: [style.icon, customStyle?.icon] }),
                React.createElement(Text, { level: level, style: [style.text, customStyle?.text], variant: "action" }, children)))));
};
export { Button };
